import { DOCUMENT, Location } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AccountJwt } from "@common/domain";
import { ACCOUNT_CONFIG, AccountConfig } from "@youm-front/account/data-access";
import {
  AccountEntity,
  AccountEntityRaw,
  Conf2AccountSignInRequestDTO,
  Conf2AccountSignInResponseDTO,
  Conf2AccountSignInResponseDTORaw,
  GuestAccountSignInRequestDTO,
  GuestAccountSignInResponseDTO,
  KeycloakAccountSignInRequestDTO,
  KeycloakAccountSignInResponseDTO
} from "@youm-front/account/domain";
import { ApiService } from "@youm-front/api/data-access";
import { Observable } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";

@Injectable()
export class AccountService {
  private readonly authRedirectQueryParam = "authRedirect";

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(ACCOUNT_CONFIG) private readonly accountConfig: AccountConfig,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly apiService: ApiService,
    private readonly location: Location
  ) {}

  conf2AccountSignIn(
    conf2AccountSignInRequestDTO: Conf2AccountSignInRequestDTO
  ): Observable<Conf2AccountSignInResponseDTO> {
    return this.apiService
      .post<Conf2AccountSignInResponseDTORaw>(
        "conf2/sign-in",
        conf2AccountSignInRequestDTO
      )
      .pipe(map((_) => new Conf2AccountSignInResponseDTO(_)));
  }

  keycloakAccountSignIn(
    keycloakAccountSignInRequestDTO: KeycloakAccountSignInRequestDTO
  ): Observable<KeycloakAccountSignInResponseDTO> {
    return this.apiService
      .post<{
        token: AccountJwt;
      }>("keycloak/sign-in", {
        token: keycloakAccountSignInRequestDTO.value.token
      })
      .pipe(
        map(
          (_) =>
            new KeycloakAccountSignInResponseDTO({
              token: _.token
            })
        )
      );
  }

  guestAccountSignIn(
    guestAccountSignInRequestDTO: GuestAccountSignInRequestDTO
  ) {
    return this.apiService
      .post<{ token: AccountJwt }>("guest/sign-in", {
        identity: guestAccountSignInRequestDTO.value.identity
      })
      .pipe(
        map(
          (_) =>
            new GuestAccountSignInResponseDTO({
              token: _.token
            })
        )
      );
  }

  me(): Observable<AccountEntity> {
    return this.apiService
      .get<{ account: AccountEntityRaw }>("me")
      .pipe(map((_) => new AccountEntity(_.account)));
  }

  redirectToAuth() {
    const url = new URL(this.accountConfig.noAuthRedirectToUrl);
    url.searchParams.append(
      this.authRedirectQueryParam,
      encodeURIComponent(this.document.location.href)
    );

    this.document.location.replace(url.toString());
  }

  redirectAfterAuth() {
    return this.route.queryParamMap.pipe(
      take(1),
      map((_) => _.get(this.authRedirectQueryParam)),
      filter((_): _ is string => !!_),
      tap((_) => this.document.location.replace(decodeURIComponent(_)))
    );
  }

  removeAuthRedirectQueryParam() {
    const url = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: { [this.authRedirectQueryParam]: null },
      queryParamsHandling: "merge"
    });

    // avoid navigation, clean param only
    this.location.replaceState(url.toString());
  }
}
